import React, { createContext, useContext, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';

interface AuthContextType {
  isAuthenticated: boolean;
  user: any | null;
  loading: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { oktaAuth, authState } = useOktaAuth();
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authState) {
      setLoading(false);
      if (authState.isAuthenticated) {
        oktaAuth.getUser().then((userInfo) => {
          setUser(userInfo);
        });
      } else {
        setUser(null);
      }
    }
  }, [authState, oktaAuth]);

  // No localStorage token sync needed — apiClient.tokenGetter is wired directly
  // to oktaAuth.getAccessToken() at module level in App.tsx, so every request
  // always gets a fresh token from the Okta SDK without touching localStorage.

  const login = async () => {
    await oktaAuth.signInWithRedirect();
  };

  const logout = async () => {
    await oktaAuth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: authState?.isAuthenticated || false,
        user,
        loading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

